import axios from 'axios'

const client = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
})

// Attach access token on every request
client.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Singleton refresh promise — all concurrent 401s wait on the same
// refresh attempt to avoid racing the rotating refresh token.
let refreshPromise: Promise<string> | null = null

function performRefresh(): Promise<string> {
  if (refreshPromise) return refreshPromise

  const refreshToken = localStorage.getItem('refresh_token')
  if (!refreshToken) {
    return Promise.reject(new Error('No refresh token'))
  }

  refreshPromise = axios
    .post(`${import.meta.env.VITE_API_URL}/api/token/refresh/`, { refresh: refreshToken })
    .then((res) => {
      const newAccess = res.data.access
      const newRefresh = res.data.refresh
      localStorage.setItem('access_token', newAccess)
      if (newRefresh) {
        localStorage.setItem('refresh_token', newRefresh)
      }
      return newAccess as string
    })
    .finally(() => {
      // Clear the singleton once settled, so future 401s can refresh again
      refreshPromise = null
    })

  return refreshPromise
}

function decodeJwtExp(token: string): number | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
    return typeof json.exp === 'number' ? json.exp : null
  } catch {
    return null
  }
}

// Returns a valid (non-expired) access token, proactively refreshing when the
// current one is within 60s of expiry. Use this on the fetch()-based streaming
// paths (chat + Sage), which bypass the axios response interceptor and would
// otherwise open a long-lived stream with a token that expires mid-response.
// Shares the singleton refreshPromise above so it never races the interceptor.
export async function ensureValidAccessToken(): Promise<string> {
  const token = localStorage.getItem('access_token') ?? ''
  const exp = token ? decodeJwtExp(token) : null
  if (exp !== null) {
    const secondsLeft = exp - Date.now() / 1000
    if (secondsLeft > 60) return token // still good
  } else if (token) {
    return token // undecodable but present — let a 401 trigger the normal path
  }
  if (!localStorage.getItem('refresh_token')) return token
  try {
    return await performRefresh()
  } catch {
    return token // refresh failed; caller's 401 handling takes over
  }
}

// Force a refresh regardless of the current token's expiry — for the
// fetch()-based stream paths to call once after a 401. Returns the new access
// token, or rejects if there is no refresh token / the refresh fails.
export function forceRefreshAccessToken(): Promise<string> {
  return performRefresh()
}

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config

    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !original.url?.includes('/api/token/refresh/') &&
      !original.url?.includes('/api/token/') &&
      !original.url?.includes('/api/auth/login/')
    ) {
      original._retry = true

      try {
        const newAccess = await performRefresh()
        original.headers.Authorization = `Bearer ${newAccess}`
        return client(original)
      } catch {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        window.location.href = '/login'
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  }
)

export default client
